import { useEffect } from 'react';
import { useAccessibility } from '../context/AccessibilityContext';

interface KeyboardShortcut {
  key: string;
  altKey?: boolean;
  ctrlKey?: boolean;
  shiftKey?: boolean;
  metaKey?: boolean;
  action: () => void;
  description: string;
}

export const useKeyboardShortcuts = () => {
  const { 
    toggleAccessibilityPanel, 
    updateSetting, 
    settings,
    speakText,
    stopSpeaking,
    isSpeaking
  } = useAccessibility();

  const shortcuts: KeyboardShortcut[] = [
    {
      key: 'a',
      altKey: true,
      action: toggleAccessibilityPanel,
      description: 'Toggle accessibility panel'
    },
    {
      key: 's',
      altKey: true,
      action: () => {
        updateSetting('textToSpeech', !settings.textToSpeech);
        const message = settings.textToSpeech 
          ? 'Text-to-speech disabled' 
          : 'Text-to-speech enabled';
        if (!settings.textToSpeech) {
          speakText(message);
        }
      },
      description: 'Toggle text-to-speech'
    },
    {
      key: 'Escape',
      action: () => {
        if (isSpeaking) {
          stopSpeaking();
        }
        // Panel-ul se va închide prin event listener-ul din AccessibilityPanel
      },
      description: 'Stop speech or close panels'
    },
    {
      key: '+',
      ctrlKey: true,
      action: () => {
        const currentSize = settings.fontSize;
        const sizes = ['small', 'medium', 'large', 'extra-large'] as const;
        const currentIndex = sizes.indexOf(currentSize);
        if (currentIndex < sizes.length - 1) {
          updateSetting('fontSize', sizes[currentIndex + 1]);
          if (settings.textToSpeech) {
            speakText(`Text size increased to ${sizes[currentIndex + 1]}`);
          }
        }
      },
      description: 'Increase text size'
    },
    {
      key: '-',
      ctrlKey: true,
      action: () => {
        const currentSize = settings.fontSize;
        const sizes = ['small', 'medium', 'large', 'extra-large'] as const;
        const currentIndex = sizes.indexOf(currentSize);
        if (currentIndex > 0) {
          updateSetting('fontSize', sizes[currentIndex - 1]);
          if (settings.textToSpeech) {
            speakText(`Text size decreased to ${sizes[currentIndex - 1]}`);
          }
        }
      },
      description: 'Decrease text size'
    },
    {
      key: 'h',
      altKey: true,
      action: () => {
        const newContrast = settings.contrast === 'normal' ? 'high' : 'normal';
        updateSetting('contrast', newContrast);
        if (settings.textToSpeech) {
          speakText(`${newContrast} contrast mode activated`);
        }
      },
      description: 'Toggle high contrast'
    },
    {
      key: 'm',
      altKey: true,
      action: () => {
        updateSetting('reducedMotion', !settings.reducedMotion);
        if (settings.textToSpeech) {
          const message = settings.reducedMotion 
            ? 'Reduced motion disabled' 
            : 'Reduced motion enabled';
          speakText(message);
        }
      },
      description: 'Toggle reduced motion'
    }
  ];

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Verificăm dacă utilizatorul scrie într-un câmp de input
      const activeElement = document.activeElement;
      const isInputField = activeElement && (
        activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.getAttribute('contenteditable') === 'true'
      );

      // Nu activăm scurtăturile dacă utilizatorul scrie într-un câmp
      if (isInputField && !event.altKey && !event.ctrlKey && !event.metaKey) {
        return;
      }

      const matchingShortcut = shortcuts.find(shortcut => 
        shortcut.key.toLowerCase() === event.key.toLowerCase() &&
        !!shortcut.altKey === event.altKey &&
        !!shortcut.ctrlKey === event.ctrlKey &&
        !!shortcut.shiftKey === event.shiftKey &&
        !!shortcut.metaKey === event.metaKey
      );

      if (matchingShortcut) {
        event.preventDefault();
        event.stopPropagation();
        matchingShortcut.action();
      }
    };

    // Adăugăm listener-ul pentru evenimente de tastatură
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [settings, toggleAccessibilityPanel, updateSetting, speakText, stopSpeaking, isSpeaking]);

  // Returnăm lista de shortcuts pentru documentație
  return {
    shortcuts: shortcuts.map(({ action, ...shortcut }) => shortcut)
  };
}; 